/**
 * §P.9 · Informes.
 *
 * ## «Lo que el club necesita es decidir, no admirar»
 *
 * Es de `BFF_BO.md` §7.6 y es toda la guía de diseño de esta sección. De ahí salen
 * dos decisiones que se notan:
 *
 * - **No hay librería de gráficas.** Doce barras de ingresos se pintan con un `div`
 *   y un porcentaje de ancho, y eso es exactamente lo que hace falta para ver si el
 *   mes que viene entra más o menos. Meter 90 kB de librería para eso es admirar.
 * - **Los números van al lado de la barra, siempre.** Una gráfica de la que hay que
 *   deducir el valor pasando el ratón no sirve para decidir: sirve para mirarla.
 *
 * ## Lo emitido y lo cobrado no son la misma cifra
 *
 * Y la diferencia es lo que un club descubre tarde, cuando lleva tres meses contando
 * como ingreso lo que nunca llegó. Por eso el informe de ingresos enseña las cuatro
 * —emitido, cobrado, devuelto y pendiente— y la barra pinta **lo cobrado sobre lo
 * emitido**, que es la única de las cuatro comparaciones que dice algo.
 *
 * ## Los totales vienen aparte, y hay que dejarlos aparte
 *
 * El sobre del servidor trae `totals` fuera de `rows`, y el porqué está escrito en
 * su DTO: «en CSV, la fila de totales es exactamente lo que rompe una hoja de
 * cálculo en cuanto alguien la ordena». Lo mismo vale en pantalla: la fila de
 * totales no se mete en la tabla.
 */

export interface Periodo {
  readonly from: string | null;
  readonly to: string | null;
}

export interface Informe<Fila> {
  readonly period: Periodo;
  readonly rows: readonly Fila[];
  /** Fuera de `rows` a propósito. Ver la cabecera. */
  readonly totals: Readonly<Record<string, number>>;
}

export interface FilaDeIngresos {
  /** `YYYY-MM`. */
  readonly month: string;
  readonly invoiced_cents: number;
  readonly collected_cents: number;
  readonly failed_cents: number;
  readonly outstanding_cents: number;
  readonly joined: number;
  readonly left: number;
  readonly active_students: number;
}

export interface FilaDeAsistencia {
  readonly group_id: string;
  readonly group_name: string;
  /** ISO: 1 es lunes. Nunca `Date.getDay()`. */
  readonly weekday: number;
  readonly starts_at: string;
  readonly sessions_held: number;
  readonly marked: number;
  readonly present: number;
  readonly absent: number;
  readonly excused: number;
  /** Fracción de 0 a 1, no porcentaje. */
  readonly attendance_rate: number;
}

/** `YYYY-MM` → «septiembre de 2026», y el crudo si no se entiende. */
export function mesEnPalabras(mes: string): string {
  const [anio, num] = mes.split('-');
  const n = Number(num);
  if (!anio || !Number.isInteger(n) || n < 1 || n > 12) return mes;

  const nombres = [
    'enero',
    'febrero',
    'marzo',
    'abril',
    'mayo',
    'junio',
    'julio',
    'agosto',
    'septiembre',
    'octubre',
    'noviembre',
    'diciembre',
  ];
  return `${nombres[n - 1]} de ${anio}`;
}

/** `2026-09` → «sep 26», para el eje de una gráfica donde no cabe el mes entero. */
export function mesCorto(mes: string): string {
  const largo = mesEnPalabras(mes);
  if (largo === mes) return mes;
  const [nombre, , anio] = largo.split(' ');
  return `${nombre!.slice(0, 3)} ${anio!.slice(2)}`;
}

/**
 * Qué parte del ancho ocupa un valor, como porcentaje.
 *
 * **Sin techo no hay barra**: con `maximo` a 0 devuelve 0 y no `NaN`. Un `width:
 * NaN%` no es un error visible — el navegador lo ignora y la barra sale del ancho
 * del contenedor, así que el mes vacío se pinta a tope y parece el mejor del año.
 */
export const anchoDeBarra = (valor: number, maximo: number): number =>
  maximo <= 0 ? 0 : Math.round((Math.max(valor, 0) / maximo) * 100);

/**
 * El porcentaje de asistencia, o `null`.
 *
 * `attendance_rate` viene como fracción y el servidor manda `null`-ish (0) cuando no
 * se pasó lista: **sin nadie marcado no hay porcentaje**, y un 0 % en una franja
 * donde nadie pasó lista acusa al grupo de no venir cuando el que no vino fue el
 * dato. Se mira `marked`, no la fracción.
 */
export const porcentajeDeAsistencia = (f: {
  marked: number;
  attendance_rate: number;
}): number | null =>
  f.marked === 0 ? null : Math.round(f.attendance_rate * 100);

/**
 * Cuánto se cobró de lo emitido, como porcentaje, o `null`.
 *
 * `null` cuando no se emitió nada: «0 % cobrado» en un mes sin recibos es una mala
 * noticia inventada.
 */
export const porcentajeCobrado = (f: {
  invoiced_cents: number;
  collected_cents: number;
}): number | null =>
  f.invoiced_cents === 0
    ? null
    : Math.round((f.collected_cents / f.invoiced_cents) * 100);

/* ── §P.9 fase 2 · Los cinco informes que quedaban ───────────────────────── */

export interface FilaDeBajas {
  /** `YYYY-MM`. */
  readonly month: string;
  readonly leave_reason: string;
  readonly students: number;
  /**
   * **Lo que convierte el informe en una decisión.**
   *
   * «Perder alumnos a los dos meses es un problema de acogida; a los dos años es la
   * vida normal de un alumno.» Sin esta columna, una lista de bajas solo da malas
   * noticias; con ella dice cuál es tu problema.
   */
  readonly avg_months_enrolled: number | null;
}

/** Un motivo de baja escrito por una persona, más el «sin motivo» del servidor. */
export const etiquetaDeMotivoDeBaja = (motivo: string): string =>
  motivo === 'sin_motivo' ? 'Sin motivo anotado' : motivo;

/**
 * Si una baja es temprana, en el sentido que importa.
 *
 * Tres meses es el corte: por debajo, el club perdió a alguien **antes de que llegara
 * a engancharse**, y eso no se arregla con más clases sino con la acogida. Es la única
 * lectura de este informe que lleva a hacer algo distinto mañana.
 */
export const BAJA_TEMPRANA_MESES = 3;

export const esBajaTemprana = (f: FilaDeBajas): boolean =>
  f.avg_months_enrolled !== null && f.avg_months_enrolled < BAJA_TEMPRANA_MESES;

/**
 * Los euros por hora de un grupo, o `null` si no se puede saber.
 *
 * **Sin horario no hay euros por hora**, y «0,00 €» sería mentira: dice que el grupo
 * no aporta nada cuando lo que falta es el horario. Es la misma familia que el 0 % de
 * ocupación sin aforo y que el «sin preguntar» de los consentimientos: un cero
 * inventado a partir de un dato que no existe.
 */
export const eurosPorHora = (f: {
  weekly_hours: number;
  cents_per_hour: number;
}): number | null => (f.weekly_hours <= 0 ? null : f.cents_per_hour);

export interface FilaDeRentabilidad {
  readonly group_id: string;
  readonly group_name: string;
  readonly venue_name: string;
  readonly students: number;
  readonly capacity: number | null;
  /**
   * La cuota de cada alumno **repartida entre los grupos a los que va**.
   *
   * «Quien paga 30 € y va a dos aporta 15 € a cada uno. Contarla entera en los dos
   * haría que la suma de los grupos duplicara los ingresos reales.» Es la clase de
   * decisión que hay que saber al leer la cifra: la suma de las filas **sí** cuadra
   * con los ingresos del mes.
   */
  readonly monthly_cents: number;
  readonly weekly_hours: number;
  /** Ingreso mensual entre las horas de tatami al mes (4,33 semanas). */
  readonly cents_per_hour: number;
}

export interface FilaDeOcupacion {
  readonly venue_name: string;
  /** ISO: 1 es lunes. */
  readonly weekday: number;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly groups: number;
  readonly capacity: number | null;
  readonly enrolled: number;
  readonly free_seats: number | null;
  /**
   * `null` cuando el club no ha puesto aforo.
   *
   * «Devolver 0 % ahí sería inventarse un aforo infinito.» La pantalla dice «sin
   * aforo puesto» en lugar de pintar una barra vacía que parece un problema.
   */
  readonly occupancy_rate: number | null;
}

/**
 * Los tres conceptos de la previsión, en español.
 *
 * Los tres, y **comprobados en la consulta**: `cuotas_activas`,
 * `pendiente_anterior` y `devoluciones_estimadas`. Es la cuarta tabla de este tipo
 * en el proyecto y la única que escribí después de mirar, no antes.
 *
 * Uno nuevo sale con su clave cruda, que en una tabla de dinero es feo pero no
 * engaña: un concepto inventado con nombre bonito sí.
 */
const CONCEPTOS: Record<string, string> = {
  cuotas_activas: 'Cuotas del mes',
  pendiente_anterior: 'Pendiente de meses anteriores',
  devoluciones_estimadas: 'Devoluciones previstas',
};

export const etiquetaDeConcepto = (concepto: string): string =>
  CONCEPTOS[concepto] ?? concepto;

export interface FilaDePrevision {
  readonly concept: string;
  /** **Negativo en las devoluciones estimadas**, para que sumen. */
  readonly cents: number;
  readonly detail: string;
}

export interface FilaDeHorasDeEntrenador {
  readonly user_id: string;
  readonly name: string;
  readonly days: number;
  /**
   * De **entradas emparejadas con salidas**.
   *
   * «Un fichaje sin salida no cuenta: es mejor una nómina corta que una inventada, y
   * lo que falta se ve en los días.» Por eso la pantalla enseña días y horas juntos:
   * la proporción entre los dos es lo que destapa los fichajes sin cerrar.
   */
  readonly hours: number;
  readonly sessions_taught: number;
}

/**
 * Horas por día trabajado, o `null` sin días.
 *
 * Es el número que destapa los fichajes sin cerrar: si un entrenador tiene 12 días y
 * 6 horas, no trabajó media hora al día — se olvidó de fichar la salida seis veces.
 * La cifra sola no lo dice; esta división sí.
 */
export const horasPorDia = (f: {
  days: number;
  hours: number;
}): number | null => (f.days === 0 ? null : f.hours / f.days);

/** Menos de una hora por día trabajado casi siempre son salidas sin fichar. */
export const SOSPECHA_HORAS_POR_DIA = 1;

/** El aforo de una franja, en palabras. `null` es «sin aforo», no cero. */
export function ocupacionEnPalabras(f: FilaDeOcupacion): string {
  if (f.capacity === null || f.occupancy_rate === null) {
    return `${f.enrolled} matriculados · sin aforo puesto`;
  }
  const pct = Math.round(f.occupancy_rate * 100);
  return `${f.enrolled} de ${f.capacity} · ${pct} %`;
}

/**
 * Dónde cabe un grupo nuevo: las franjas con sitio de sobra.
 *
 * Es el propósito declarado del informe —«dónde cabe un grupo nuevo»— y necesita el
 * aforo puesto: sin él no se sabe si sobran plazas o si el club nunca las contó.
 */
export const tieneSitio = (f: FilaDeOcupacion, minimo = 5): boolean =>
  f.free_seats !== null && f.free_seats >= minimo;

/**
 * §P.9 fase 3 · El embudo del directorio.
 *
 * Cuatro escalones, y **los cuatro son de la misma gente**: las reservas de clase de
 * prueba del periodo, cuántas vinieron, cuántas se apuntaron y cuántas de esas
 * seguían a los seis meses.
 *
 * El cuarto tiene un denominador propio —`totals.enrolled_mature`, las altas que ya
 * han cumplido seis meses— porque el tiempo se mueve: un alta de este mes no es una
 * permanencia todavía **ni es una baja**. Medirla contra todas las altas daría «0 %»
 * justo en los clubes que más crecen.
 */
export interface FilaDelEmbudo {
  readonly stage: string;
  readonly count: number;
  /** Contra el escalón de arriba. En el último, contra `enrolled_mature`. */
  readonly rate_from_previous: number | null;
  readonly rate_from_start: number | null;
}

const ESCALONES: Readonly<Record<string, string>> = {
  pruebas_solicitadas: 'Pidieron clase de prueba',
  pruebas_asistidas: 'Vinieron a probar',
  altas: 'Se apuntaron',
  siguen_a_los_6_meses: 'Seguían a los seis meses',
};

export const etiquetaDeEscalon = (escalon: string): string =>
  ESCALONES[escalon] ?? escalon;

/**
 * §P.9 fase 4 · La comparativa anonimizada.
 *
 * **La media, nunca los clubes que la componen**, y solo si hay cinco o más en el
 * tramo: con cuatro, cualquiera de los cuatro puede despejar los datos de los otros
 * tres restando los suyos. La regla vive dentro de la vista de la base de datos para
 * que ningún endpoint pueda olvidarse de ella, y con menos de cinco la respuesta es
 * `422 benchmark_not_anonymous` — no una media de cuatro.
 *
 * Por eso este informe **no usa la envoltura común**: su sobre no es `rows`/`totals`
 * y su estado normal al principio no es «vacío», es «todavía no se puede».
 */
export interface Comparativa {
  readonly size_band: string;
  /** Cuántos clubes componen la media. **Nunca menos de cinco.** */
  readonly clubs: number;
  readonly rows: readonly {
    readonly metric: string;
    readonly mine: number;
    readonly average: number;
    readonly difference: number;
  }[];
}

const METRICAS: Readonly<Record<string, string>> = {
  students: 'Alumnos',
  monthly_cents: 'Facturación mensual',
  cents_per_student: 'Cuota media por alumno',
  attendance_rate: 'Asistencia',
  churn_rate: 'Bajas al año',
};

export const etiquetaDeMetrica = (m: string): string => METRICAS[m] ?? m;

/**
 * Cómo se escribe cada métrica: unas son dinero, otras porcentaje y otras cuentas.
 *
 * Sin esto, «0.87» de asistencia y «4500» de cuota salen con la misma cara y las dos
 * se leen mal. Se decide por el nombre de la métrica porque es lo único que el
 * servidor manda — y añadir una desconocida cae en el caso de la cuenta, que es el
 * que menos miente.
 */
export function comoSeEscribe(
  metrica: string
): 'euros' | 'porcentaje' | 'cuenta' {
  if (metrica.endsWith('_cents')) return 'euros';
  if (metrica.endsWith('_rate')) return 'porcentaje';
  return 'cuenta';
}

/**
 * **Más no siempre es mejor.** Más alumnos sí; más bajas, no.
 *
 * Pintar en verde una diferencia positiva sin mirar la métrica le diría a un club que
 * va bien justo cuando se le está yendo la gente. Es el mismo error que el embudo del
 * 12 000 %, en pequeño.
 */
export const mejorEsMenos = (metrica: string): boolean =>
  metrica === 'churn_rate';
